import { useState, useMemo, useRef } from 'react'
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts'
import { useFinance } from '../../context/FinanceContext'
import { useSettings } from '../../context/SettingsContext'
import { getThemeColors } from '../../lib/constants'
import { formatCurrency, formatCurrencyExact } from '../../lib/formatters'
import { calculateInvestmentGrowth, compareInvestmentRates } from '../../lib/calculations'
import GlassCard from '../ui/GlassCard'
import Button from '../ui/Button'
import { addChartToPDF } from '../../lib/pdfChartCapture'
import { useSubscription } from '../../context/SubscriptionContext'
import { trackExportReport } from '../../lib/analytics'

const RATE_PRESETS = [
  { label: 'Conservative', rate: 4, desc: 'Bonds / CDs' },
  { label: 'Moderate', rate: 7, desc: 'Balanced portfolio' },
  { label: 'Aggressive', rate: 10, desc: 'Stock-heavy' },
  { label: 'S&P 500 Avg', rate: 10.5, desc: 'Historical average' },
]

export default function InvestmentCalculator() {
  const { state } = useFinance()
  const { settings } = useSettings()
  const { isPro } = useSubscription()
  const colors = getThemeColors(state.theme)
  const d = settings.calculatorDefaults.investment

  const [initialAmount, setInitialAmount] = useState(d.initialAmount)
  const [monthlyContribution, setMonthlyContribution] = useState(d.monthlyContribution)
  const [annualReturn, setAnnualReturn] = useState(d.annualReturn)
  const [years, setYears] = useState(d.years)
  const [showComparison, setShowComparison] = useState(true)
  const chartRef = useRef(null)
  const comparisonChartRef = useRef(null)

  const result = useMemo(() =>
    calculateInvestmentGrowth(initialAmount, monthlyContribution, annualReturn, years),
    [initialAmount, monthlyContribution, annualReturn, years]
  )

  const comparison = useMemo(() =>
    compareInvestmentRates(initialAmount, monthlyContribution, years, RATE_PRESETS.map(p => p.rate)),
    [initialAmount, monthlyContribution, years]
  )

  const growthPercent = result.totalContributions > 0
    ? ((result.totalGrowth / result.totalContributions) * 100).toFixed(0)
    : 0

  // PDF Export
  const handleExportPDF = async () => {
    trackExportReport()
    const { default: jsPDF } = await import('jspdf')
    const { default: autoTable } = await import('jspdf-autotable')

    const doc = new jsPDF()
    const margin = 14

    // ── Title ──
    doc.setFontSize(22)
    doc.setTextColor(30, 41, 59)
    doc.text('Investment Growth Report', margin, 22)
    doc.setFontSize(10)
    doc.setTextColor(100, 116, 139)
    doc.text(`Generated by Liquid Finance  •  ${new Date().toLocaleDateString()}`, margin, 30)

    // ── Return Rate Presets ──
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Return Rate Presets', margin, 42)

    autoTable(doc, {
      head: [['Strategy', 'Rate', 'Description']],
      body: RATE_PRESETS.map(p => [
        p.label,
        `${p.rate}%`,
        p.desc,
      ]),
      startY: 46,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: margin, right: margin },
    })
    const selectedPreset = RATE_PRESETS.find(p => p.rate === annualReturn)
    if (selectedPreset) {
      const presetY = doc.lastAutoTable.finalY + 4
      doc.setFontSize(9)
      doc.setTextColor(100, 116, 139)
      doc.text(`Selected: ${selectedPreset.label} (${selectedPreset.rate}% - ${selectedPreset.desc})`, margin, presetY)
    }

    // ── Investment Parameters ──
    let y = (doc.lastAutoTable?.finalY || 80) + 14
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Investment Parameters', margin, y)

    autoTable(doc, {
      head: [['Parameter', 'Value']],
      body: [
        ['Initial Investment', formatCurrency(initialAmount)],
        ['Monthly Contribution', formatCurrency(monthlyContribution)],
        ['Annual Return Rate', `${annualReturn}%`],
        ['Time Horizon', `${years} years`],
      ],
      startY: y + 4,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 9 },
      margin: { left: margin, right: margin },
    })

    // ── Result Cards ──
    y = (doc.lastAutoTable?.finalY || 80) + 10
    doc.setFontSize(14)
    doc.setTextColor(30, 41, 59)
    doc.text('Results Summary', margin, y)

    autoTable(doc, {
      head: [['Metric', 'Value']],
      body: [
        ['Final Balance', formatCurrency(result.finalBalance)],
        ['Total Contributed', formatCurrency(result.totalContributions)],
        ['Investment Growth', formatCurrency(result.totalGrowth)],
        ['Growth Multiple', `${growthPercent}%`],
      ],
      startY: y + 4,
      theme: 'grid',
      headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
      styles: { fontSize: 10 },
      margin: { left: margin, right: margin },
      columnStyles: { 1: { fontStyle: 'bold' } },
    })

    // ── Growth Projection Chart ──
    if (result.schedule.length > 0) {
      await addChartToPDF(doc, chartRef, { title: 'Growth Projection' })
    }

    // ── Yearly Growth Schedule ──
    if (result.schedule.length > 0) {
      doc.addPage()
      doc.setFontSize(16)
      doc.setTextColor(30, 41, 59)
      doc.text('Yearly Growth Schedule', margin, 22)

      autoTable(doc, {
        head: [['Year', 'Balance', 'Total Contributions', 'Total Growth', 'Annual Growth']],
        body: result.schedule.map((r, i) => {
          const prevBalance = i > 0 ? result.schedule[i - 1].balance : initialAmount
          const annualGrowthAmt = r.balance - prevBalance - (monthlyContribution * 12)
          return [
            `Year ${r.year}`,
            formatCurrency(r.balance),
            formatCurrency(r.contributions),
            formatCurrency(r.growth),
            formatCurrency(Math.max(0, annualGrowthAmt)),
          ]
        }),
        startY: 28,
        theme: 'striped',
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 8 },
        margin: { left: margin, right: margin },
      })
    }

    // ── Rate Comparison Section ──
    if (comparison.length > 0) {
      doc.addPage()
      doc.setFontSize(16)
      doc.setTextColor(30, 41, 59)
      doc.text('Compare Return Rates', margin, 22)

      autoTable(doc, {
        head: [['Strategy', 'Rate', 'Final Balance', 'Total Contributions', 'Total Growth', 'Growth %']],
        body: comparison.map((c, i) => {
          const gPct = c.totalContributions > 0 ? ((c.totalGrowth / c.totalContributions) * 100).toFixed(0) : '0'
          return [
            RATE_PRESETS[i].label,
            `${c.rate}%`,
            formatCurrency(c.finalBalance),
            formatCurrency(c.totalContributions),
            formatCurrency(c.totalGrowth),
            `${gPct}%`,
          ]
        }),
        startY: 28,
        theme: 'grid',
        headStyles: { fillColor: [30, 41, 59], textColor: 255, fontStyle: 'bold' },
        styles: { fontSize: 9 },
        margin: { left: margin, right: margin },
      })

      // Comparison summary cards
      y = (doc.lastAutoTable?.finalY || 60) + 8
      doc.setFontSize(11)
      doc.setTextColor(30, 41, 59)
      doc.text('Strategy Comparison Summary', margin, y)
      y += 6
      comparison.forEach((c, i) => {
        doc.setFontSize(9)
        doc.setTextColor(100, 116, 139)
        doc.text(`${RATE_PRESETS[i].label} (${c.rate}%):  ${formatCurrency(c.finalBalance)}  (+${formatCurrency(c.totalGrowth)} growth)`, margin + 2, y)
        y += 5
      })

      // Comparison bar chart
      await addChartToPDF(doc, comparisonChartRef, { title: 'Rate Comparison Chart' })
    }

    doc.save(`investment-growth-${Date.now()}.pdf`)
  }

  return (
    <div className="space-y-6">
      {/* Rate Presets */}
      <GlassCard>
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-sm font-semibold text-body">Return Rate Presets</h3>
          <Button variant="secondary" size="sm" onClick={handleExportPDF} disabled={!isPro} title={!isPro ? 'Pro feature' : ''}>
            Export PDF{!isPro ? ' (Pro)' : ''}
          </Button>
        </div>
        <div className="flex flex-wrap gap-2">
          {RATE_PRESETS.map(p => (
            <button
              key={p.rate}
              onClick={() => setAnnualReturn(p.rate)}
              className={`flex flex-col px-4 py-2.5 rounded-xl text-sm transition-all ${
                annualReturn === p.rate
                  ? 'text-heading shadow-sm'
                  : 'text-muted hover:text-heading bg-hover'
              }`}
              style={annualReturn === p.rate ? {
                background: `linear-gradient(135deg, ${colors.primary}22, ${colors.secondary}22)`,
                border: `1px solid ${colors.primary}44`,
              } : {}}
            >
              <span className="font-medium">{p.label}</span>
              <span className="text-xs text-faint">{p.rate}% - {p.desc}</span>
            </button>
          ))}
        </div>
      </GlassCard>

      {/* Inputs */}
      <GlassCard>
        <h3 className="text-sm font-semibold text-body mb-4">Investment Details</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <InputField
            label="Initial Investment"
            value={initialAmount}
            onChange={setInitialAmount}
            prefix="$"
            type="number"
            min={0}
          />
          <InputField
            label="Monthly Contribution"
            value={monthlyContribution}
            onChange={setMonthlyContribution}
            prefix="$"
            type="number"
            min={0}
          />
          <InputField
            label="Annual Return"
            value={annualReturn}
            onChange={setAnnualReturn}
            suffix="%"
            type="number"
            step={0.1}
            min={0}
          />
          <InputField
            label="Time Horizon"
            value={years}
            onChange={setYears}
            suffix="years"
            type="number"
            min={1}
            max={50}
          />
        </div>
      </GlassCard>

      {/* Results */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <ResultCard label="Final Balance" value={formatCurrency(result.finalBalance)} color={colors.primary} />
        <ResultCard label="Total Contributed" value={formatCurrency(result.totalContributions)} color={colors.secondary} />
        <ResultCard label="Investment Growth" value={formatCurrency(result.totalGrowth)} color={colors.positive} />
        <ResultCard label="Growth Multiple" value={`${growthPercent}%`} color={colors.warning} />
      </div>

      {/* Growth Chart */}
      {result.schedule.length > 0 && (
        <GlassCard>
          <h3 className="text-sm font-semibold text-body mb-4">Growth Projection</h3>
          <div ref={chartRef} className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={result.schedule}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                <XAxis
                  dataKey="year"
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => `Yr ${v}`}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.chartTick }}
                  tickFormatter={v => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}k`}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'var(--card-bg)',
                    border: '1px solid var(--card-border)',
                    borderRadius: '12px',
                    backdropFilter: 'blur(12px)',
                    color: 'var(--heading)',
                  }}
                  formatter={(v) => formatCurrencyExact(v)}
                />
                <Legend />
                <Area type="monotone" dataKey="contributions" name="Contributions" stroke={colors.secondary} fill={colors.secondary} fillOpacity={0.2} strokeWidth={2} stackId="a" />
                <Area type="monotone" dataKey="growth" name="Growth" stroke={colors.positive} fill={colors.positive} fillOpacity={0.2} strokeWidth={2} stackId="a" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassCard>
      )}

      {/* Rate Comparison */}
      <GlassCard className="!p-0 overflow-hidden">
        <button
          onClick={() => setShowComparison(!showComparison)}
          className="w-full flex items-center justify-between px-6 py-4 text-sm font-semibold text-body hover:bg-subtle transition"
        >
          <span>Compare Return Rates</span>
          <svg className={`w-4 h-4 text-muted transition-transform ${showComparison ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </button>

        {showComparison && (
          <div className="px-6 pb-6">
            <div ref={comparisonChartRef} className="h-64 mb-4">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={comparison.map((c, i) => ({
                  name: RATE_PRESETS[i].label,
                  rate: `${c.rate}%`,
                  contributions: c.totalContributions,
                  growth: c.totalGrowth,
                }))}>
                  <CartesianGrid strokeDasharray="3 3" stroke={colors.chartGrid} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: colors.chartTick }} />
                  <YAxis
                    tick={{ fontSize: 11, fill: colors.chartTick }}
                    tickFormatter={v => v >= 1000000 ? `$${(v / 1000000).toFixed(1)}M` : `$${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--card-bg)',
                      border: '1px solid var(--card-border)',
                      borderRadius: '12px',
                      backdropFilter: 'blur(12px)',
                      color: 'var(--heading)',
                    }}
                    formatter={(v) => formatCurrency(v)}
                  />
                  <Legend />
                  <Bar dataKey="contributions" name="Contributions" fill={colors.secondary} radius={[4, 4, 0, 0]} stackId="a" />
                  <Bar dataKey="growth" name="Growth" fill={colors.positive} radius={[4, 4, 0, 0]} stackId="a" />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              {comparison.map((c, i) => (
                <div key={c.rate} className="p-3 rounded-xl bg-subtle">
                  <p className="text-xs text-muted">{RATE_PRESETS[i].label} ({c.rate}%)</p>
                  <p className="text-lg font-bold text-heading mt-1">{formatCurrency(c.finalBalance)}</p>
                  <p className="text-xs text-positive mt-0.5">+{formatCurrency(c.totalGrowth)} growth</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </GlassCard>
    </div>
  )
}

function InputField({ label, value, onChange, prefix, suffix, type = 'text', step, min, max }) {
  return (
    <div>
      <label className="block text-xs font-medium text-muted mb-1.5">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-faint">{prefix}</span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value)}
          step={step}
          min={min}
          max={max}
          className={`w-full bg-input border border-input rounded-xl py-2.5 text-sm text-heading focus:outline-none focus:ring-1 ${
            prefix ? 'pl-7 pr-3' : suffix ? 'pl-3 pr-16' : 'px-3'
          }`}
        />
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-faint">{suffix}</span>
        )}
      </div>
    </div>
  )
}

function ResultCard({ label, value, color }) {
  return (
    <GlassCard className="!p-4">
      <p className="text-xs text-muted mb-1">{label}</p>
      <p className="text-lg font-bold" style={{ color }}>{value}</p>
    </GlassCard>
  )
}
